import { jsx as _jsx } from "react/jsx-runtime";
import React from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAltSharp';
import KeyboardReturnIcon from '@mui/icons-material/KeyboardReturn';
import { SvgIcon, styled, useTheme } from '@mui/material';
import { hexToRgb } from '../../../utils';
const StyledSpan = styled('span', {
    shouldForwardProp: prop => prop !== 'color' && prop !== 'disabled',
}) `
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  height: 24px;
  width: 24px;
  background-color: ${({ theme, color, disabled }) => {
    if (disabled) {
        return `${theme.palette.action.disabledBackground};`;
    }
    else
        return `rgba(${hexToRgb(color)}, ${theme.palette.action.focusVisibleOpacity});`;
}}
  font-size: 12px !important;
  letter-spacing: 0.4px;
  border-radius: 4px;
  text-transform: none;
`;
const ShortcutKey = React.memo(({ variant = 'outlined', size = 'medium', color, keyToShow, keysToPress, onPress = () => { }, disabled = false, scopes, }) => {
    const theme = useTheme();
    const enabledKeysOnForms = [
        'up',
        'down',
        'return',
        'enter',
        'esc',
        'meta',
        'ctrl',
    ];
    // normally we would want to disable a hotkey if we are in a form.
    // this logic provides exceptions when non-characters are included in the hotkeys.
    // This will come in handy particularly when we have inputted in a form and want to use the arrow keys to navigate.
    let formTags;
    enabledKeysOnForms.every((key) => {
        if (keysToPress?.includes(key)) {
            formTags = ['input', 'textarea', 'select'];
            return false;
        }
        return true;
    });
    const handleKeyPress = (e) => {
        onPress?.(e);
    };
    useHotkeys(keysToPress ?? '', handleKeyPress, {
        enabled: !disabled,
        scopes: scopes ?? 'main',
        enableOnFormTags: formTags,
    });
    let iconColor = variant === 'contained' ?
        theme.palette.common.white :
        color;
    if (disabled) {
        iconColor = theme.palette.action.disabled;
    }
    ;
    const iconSizeMap = {
        small: 16,
        medium: 16,
        large: 18,
        xlarge: 20,
        giant: 24,
    };
    const fontNumber = iconSizeMap[size] ?? 16;
    const fontSize = `${fontNumber}px`;
    const shortcutKeyMap = {
        right: (_jsx(ArrowRightAltIcon, { htmlColor: iconColor, sx: { fontSize } })),
        left: (_jsx(ArrowRightAltIcon, { htmlColor: iconColor, sx: { fontSize, transform: 'rotate(180deg)' } })),
        up: (_jsx(ArrowRightAltIcon, { htmlColor: iconColor, sx: { fontSize, transform: 'rotate(270deg)' } })),
        down: (_jsx(ArrowRightAltIcon, { htmlColor: iconColor, sx: { fontSize, transform: 'rotate(90deg)' } })),
        return: (_jsx(KeyboardReturnIcon, { htmlColor: iconColor, sx: { fontSize } })),
        enter: (_jsx(KeyboardReturnIcon, { htmlColor: iconColor, sx: { fontSize } })),
        // our 'ShiftSharp' icon isn't present in the MUI library, so we have to create it ourselves.
        shift: (_jsx(SvgIcon, { htmlColor: iconColor, sx: { fontSize }, children: _jsx("svg", { fill: "none", xmlns: "http://www.w3.org/2000/svg", viewBox: '0 0 24 24', children: _jsx("path", { d: "M8.16108 18.9346L8.16112 19.5598L8.78633 19.5596L15.2002 19.5571L15.8236 19.5569L15.825 18.9335L15.8379 13.1904H19.5H21.0089L19.9419 12.1235L12.4419 4.62349L12 4.18155L11.5581 4.62349L4.05806 12.1235L2.99112 13.1904H4.5H8.16075L8.16108 18.9346Z", stroke: iconColor, strokeWidth: "1.5" }) }) })),
        esc: _jsx("span", { style: { fontSize: `'${fontNumber / 2}px'`, fontWeight: '500' }, children: "esc" }),
    };
    const keyContent = shortcutKeyMap?.[keyToShow] ?? keyToShow.toUpperCase()[0];
    return (_jsx(StyledSpan, { translate: "no", color: iconColor, disabled: disabled, className: 'shortcut-key-span', children: keyContent }));
});
ShortcutKey.displayName = 'ShortcutKey';
export default ShortcutKey;
//# sourceMappingURL=index.js.map